import { onError } from '@apollo/client/link/error'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { processEnv } from 'components/configuration/environmentVariables'

const MAX_VARIABLES_LENGTH = 1024

/** HTTP 499: Client Closed Request — indicates the browser disconnected before the server responded */
const HTTP_STATUS_CLIENT_DISCONNECTED = 499

const APP_INSIGHTS_EVENT_NAME = 'GraphQL_499_ClientDisconnected'

let appInsightsInstance: ApplicationInsights | undefined

const getAppInsights = (): ApplicationInsights | undefined => {
    if (appInsightsInstance) return appInsightsInstance

    const key = processEnv.REACT_APP_AppInsightInstrumentationKey
    if (!key) return undefined

    appInsightsInstance = new ApplicationInsights({
        config: { instrumentationKey: key },
    })
    appInsightsInstance.loadAppInsights()
    return appInsightsInstance
}

/**
 * Creates an Apollo Link that intercepts network errors on GraphQL operations.
 * All network errors are logged to the console. HTTP 499 errors (client disconnected)
 * are additionally tracked as custom events in Application Insights for alerting and dashboarding.
 */
export const createNetworkErrorLink = () =>
    onError(({ operation, networkError }) => {
        if (!networkError) {
            return
        }

        const statusCode =
            'statusCode' in networkError
                ? (networkError as { statusCode: number }).statusCode
                : undefined

        const timestamp = new Date().toISOString()

        console.error('[GraphQL Network Error]', {
            operationName: operation.operationName,
            statusCode,
            message: networkError.message,
            browserOnline: navigator.onLine,
            timestamp,
        })

        if (statusCode === HTTP_STATUS_CLIENT_DISCONNECTED) {
            const variables = operation.variables
                ? JSON.stringify(operation.variables).substring(
                      0,
                      MAX_VARIABLES_LENGTH
                  )
                : ''

            getAppInsights()?.trackEvent({
                name: APP_INSIGHTS_EVENT_NAME,
                properties: {
                    operationName: operation.operationName,
                    variables,
                    statusCode: String(statusCode),
                    browserOnline: String(navigator.onLine),
                    timestamp,
                    url: processEnv.REACT_APP_GraphQLUri,
                },
            })
        }
    })
